import apiClient from "@shared/services/apiClient";
import logger from "@shared/utils/logger";

export interface AppVersionInfo {
  version: string;
  build: string;
  releaseDate: string;
  releaseType?: string;
  sourceSha?: string;
}

const GENERIC_BUILD_LABELS = new Set([
  "DEV",
  "UNKNOWN",
  "development",
  "local",
  "staging",
  "production",
  "test",
]);

function readStringEnv(value: unknown): string | null {
  if (typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function readGitSha(value: unknown): string | null {
  const candidate = readStringEnv(value);
  if (!candidate) {
    return null;
  }

  const match = candidate.match(/(?:^|sha-)([0-9a-f]{7,40})$/i);
  return match ? match[1].toLowerCase() : null;
}

function resolveSourceSha(...candidates: unknown[]): string | null {
  for (const candidate of candidates) {
    const sha = readGitSha(candidate);
    if (sha) {
      return sha;
    }
  }
  return null;
}

function buildTimeAppVersionInfo(): AppVersionInfo {
  const version =
    readStringEnv(__APP_VERSION__) ||
    readStringEnv(import.meta.env.VITE_APP_VERSION) ||
    readStringEnv(import.meta.env.VITE_VERSION) ||
    "UNKNOWN";
  const build =
    readStringEnv(__APP_BUILD__) ||
    readStringEnv(import.meta.env.VITE_APP_BUILD) ||
    readStringEnv(import.meta.env.VITE_BUILD) ||
    readStringEnv(import.meta.env.MODE) ||
    "DEV";
  const sourceSha = resolveSourceSha(
    __APP_SOURCE_SHA__,
    import.meta.env.VITE_APP_SOURCE_SHA,
    build,
  );
  const releaseDate =
    readStringEnv(__APP_RELEASE_DATE__) ||
    readStringEnv(import.meta.env.VITE_APP_RELEASE_DATE) ||
    new Date().toISOString();
  const releaseType =
    readStringEnv(__APP_RELEASE_TYPE__) ||
    readStringEnv(import.meta.env.VITE_APP_RELEASE_TYPE);

  return {
    version,
    build,
    releaseDate,
    ...(releaseType ? { releaseType } : {}),
    ...(sourceSha ? { sourceSha } : {}),
  };
}

function shouldPreferBuildTimeBuild(build: string): boolean {
  return !GENERIC_BUILD_LABELS.has(build);
}

export function mergeAppVersionInfo(
  value: unknown,
  fallback: AppVersionInfo = BUILD_TIME_APP_INFO,
): AppVersionInfo | null {
  if (!value || typeof value !== "object") {
    return null;
  }

  // When the SPA bundle already carries a concrete build label, treat the
  // entire bundle metadata blob as authoritative. Mixing backend release dates
  // or source SHAs into the footer creates contradictory portal metadata after
  // login because /api/version/current describes systemapi, not the deployed
  // serviceapi bundle.
  if (shouldPreferBuildTimeBuild(fallback.build)) {
    return {
      version: fallback.version,
      build: fallback.build,
      releaseDate: fallback.releaseDate,
      ...(fallback.releaseType ? { releaseType: fallback.releaseType } : {}),
      ...(fallback.sourceSha ? { sourceSha: fallback.sourceSha } : {}),
    };
  }

  const candidate = value as Partial<AppVersionInfo>;
  const version = readStringEnv(candidate.version) || fallback.version;
  const candidateBuild = readStringEnv(candidate.build);
  const build = candidateBuild || fallback.build;
  const releaseDate =
    readStringEnv(candidate.releaseDate) || fallback.releaseDate;
  const releaseType =
    readStringEnv(candidate.releaseType) || fallback.releaseType;
  const sourceSha = resolveSourceSha(
    (candidate as { sourceSha?: unknown }).sourceSha,
    candidateBuild,
    fallback.sourceSha,
    fallback.build,
  );

  return {
    version,
    build,
    releaseDate,
    ...(releaseType ? { releaseType } : {}),
    ...(sourceSha ? { sourceSha } : {}),
  };
}

function formatReleaseDateLabel(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "Unknown";
  }

  return date.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });
}

const BUILD_TIME_APP_INFO = buildTimeAppVersionInfo();

let cachedVersion: AppVersionInfo | null = null;
let cachedVersionPromise: Promise<AppVersionInfo | null> | null = null;

async function loadAppVersionInfo(): Promise<AppVersionInfo | null> {
  if (cachedVersion) {
    return cachedVersion;
  }

  if (cachedVersionPromise) {
    return cachedVersionPromise;
  }

  cachedVersionPromise = apiClient
    .get("/version/current")
    .then((response) => {
      const payload = response.data?.success
        ? response.data.data
        : response.data?.data || response.data;
      cachedVersion = mergeAppVersionInfo(payload);
      return cachedVersion;
    })
    .catch((error) => {
      logger.error("Failed to fetch version", error);
      return null;
    })
    .finally(() => {
      cachedVersionPromise = null;
    });

  return cachedVersionPromise;
}

export const getAppVersionInfo = async (): Promise<AppVersionInfo | null> => {
  const versionInfo = await loadAppVersionInfo();
  return versionInfo ? { ...versionInfo } : null;
};

export const getBuildTimeAppVersionInfo = (): AppVersionInfo => ({
  ...BUILD_TIME_APP_INFO,
});

/**
 * Fetch application version from backend API
 * Returns cached version after first fetch for performance
 */
export const getAppVersion = async (): Promise<string> => {
  return (await loadAppVersionInfo())?.version || BUILD_TIME_APP_INFO.version;
};

export const getAppBuild = async (): Promise<string> => {
  return (await loadAppVersionInfo())?.build || BUILD_TIME_APP_INFO.build;
};

export const getAppReleaseDate = async (): Promise<string> => {
  return formatReleaseDateLabel(
    (await loadAppVersionInfo())?.releaseDate || BUILD_TIME_APP_INFO.releaseDate,
  );
};

// Synchronous fallback for backwards compatibility (deprecated)
export const APP_VERSION = BUILD_TIME_APP_INFO.version;
export const APP_RELEASE_DATE = formatReleaseDateLabel(
  BUILD_TIME_APP_INFO.releaseDate,
);
